import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';
import { AdminChartsDataDto } from '../../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface AdminChartsProps {
  chartsData: AdminChartsDataDto;
  isLoading?: boolean;
}

const AdminCharts: React.FC<AdminChartsProps> = ({ chartsData, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-64 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Work Orders by Status Pie Chart
  const statusChartData = {
    labels: chartsData.workOrdersByStatus.map(item => item.label),
    datasets: [
      {
        data: chartsData.workOrdersByStatus.map(item => item.value),
        backgroundColor: chartsData.workOrdersByStatus.map(item => item.color || '#3B82F6'),
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: false,
      },
    },
  };

  // Work Orders Trend Line Chart
  const trendChartData = {
    labels: chartsData.workOrdersTrend.map(item => item.label || item.date),
    datasets: [
      {
        label: 'Work Orders Created',
        data: chartsData.workOrdersTrend.map(item => item.value),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Work Orders by Priority Bar Chart
  const priorityChartData = {
    labels: chartsData.workOrdersByPriority.map(item => item.label),
    datasets: [
      {
        label: 'Work Orders',
        data: chartsData.workOrdersByPriority.map(item => item.value),
        backgroundColor: chartsData.workOrdersByPriority.map(item => item.color || '#10B981'),
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const priorityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Company Activity Bar Chart
  const companyChartData = {
    labels: chartsData.companyActivity.map(item => item.label),
    datasets: [
      {
        label: 'Work Orders',
        data: chartsData.companyActivity.map(item => item.value),
        backgroundColor: '#8B5CF6',
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const companyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // User Agents by Device Pie Chart
  const userAgentChartData = {
    labels: chartsData.userAgentsByDevice.map(item => item.label),
    datasets: [
      {
        data: chartsData.userAgentsByDevice.map(item => item.value),
        backgroundColor: chartsData.userAgentsByDevice.map(item => item.color || '#3B82F6'),
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const userAgentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Top Row - Status and Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Work Orders by Status</CardTitle>
          </CardHeader>
          <CardContent>
            {chartsData.workOrdersByStatus.length > 0 ? (
              <div className="h-64">
                <Pie data={statusChartData} options={statusChartOptions} />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                No status data available for the selected range.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Work Orders Trend (12 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Line data={trendChartData} options={trendChartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row - Priority and Company Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Work Orders by Priority</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar data={priorityChartData} options={priorityChartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Company Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar data={companyChartData} options={companyChartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Third Row - User Agent Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>User Agents by Device</CardTitle>
          </CardHeader>
          <CardContent>
            {chartsData.userAgentsByDevice.length > 0 ? (
              <div className="h-64">
                <Pie data={userAgentChartData} options={userAgentChartOptions} />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                No user agent data available for the selected range.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Placeholder for future chart */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
              Additional metrics will be displayed here.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCharts;
